import { localeAtom } from '@repo/i18n';
import {
    NativeCalculateResult,
    NativeMember,
    NativeMemberCardProduct,
    NativeMemberLoginResponse,
    NativeMemberRegisterRequest,
    NativeMemberRegisterResponse,
    NativeReserveCalculateRequest,
} from '@repo/types';
import { configurationAtom } from '../../widgetsConfiguration';
import { get, getRequest, post } from '../utils/api-client-common';

// Native membership talks directly to Bilberry Base (config.bilberryBaseApiUrl),
// unlike the legacy membership-api fetcher. Member-authenticated endpoints take
// the PAT explicitly and send it as a Bearer override: getRequest applies
// additionalHeaders via headers.set AFTER the tenant client token, so the member
// token replaces it in the single-valued Authorization header. Guests/legacy are
// untouched because no token is passed.
function memberAuthHeaders(token: string) {
    return { Authorization: `Bearer ${token}` };
}

export async function nativeMemberLogin(
    email: string,
    password: string,
): Promise<NativeMemberLoginResponse> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest('/api/member/auth/login', locale, config);
    const body = await post(url, { email, password }, headers);
    return body as NativeMemberLoginResponse;
}

// Self-registration (auto-login). Public like /login — Base resolves the tenant from its
// own instance and does not read the client token here, so the standard header plumbing is
// harmless. Returns the same { token, member } envelope as login. Base validation surfaces
// as a 422 (ApiError.fieldErrors); duplicate email is reported (not anti-enumeration) so the
// UI can steer the visitor to log in instead.
export async function nativeMemberRegister(
    payload: NativeMemberRegisterRequest,
): Promise<NativeMemberRegisterResponse> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest('/api/member/auth/register', locale, config);
    const body = await post(url, payload, headers);
    return body as NativeMemberRegisterResponse;
}

export async function nativeMemberMe(token: string): Promise<NativeMember> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(
        '/api/member/auth/me',
        locale,
        config,
        {},
        memberAuthHeaders(token),
    );
    const body = await get<{ member: NativeMember }>(url, headers);
    return body.member;
}

export async function nativeMemberLogout(token: string): Promise<void> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(
        '/api/member/auth/logout',
        locale,
        config,
        {},
        memberAuthHeaders(token),
    );
    await post(url, undefined, headers, undefined, false);
}

export async function nativeMemberForgotPassword(email: string): Promise<void> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest('/api/member/auth/forgot-password', locale, config);
    await post(url, { email }, headers, undefined, false);
}

// Purchasable member-card catalog. Public (client_or_member): the client token serves
// anonymous discovery; a logged-in member's PAT works on the same call. Same data either way.
export async function getNativeMemberCardProducts(): Promise<NativeMemberCardProduct[]> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest('/api/member-card-products', locale, config);
    const body = await get<{ data: NativeMemberCardProduct[] }>(url, headers);
    return body.data;
}

// Mints a one-time (~60s) SSO token and returns the Base My-page URL to open in a new tab.
export async function nativeMemberHandoff(token: string): Promise<string> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(
        '/api/member/auth/handoff',
        locale,
        config,
        {},
        memberAuthHeaders(token),
    );
    const body = await post(url, undefined, headers);
    return body.url as string;
}

// Member-card coverage preview for the activity/timeslot checkout. Advisory only — the same
// allocator runs authoritatively at reserve, so preview == charge. Returns whole-NOK totals.
export async function nativeReserveCalculate(
    request: NativeReserveCalculateRequest,
    token: string,
): Promise<NativeCalculateResult> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(
        '/api/reserve/calculate',
        locale,
        config,
        {},
        memberAuthHeaders(token),
    );
    const res = await post(url, request, headers);
    // total drives both the displayed and charged amount, so fail loudly on a malformed envelope
    // rather than letting a bad shape flow through the blind cast below.
    if (typeof res?.data?.total !== 'number') {
        throw new Error('Unexpected /api/reserve/calculate response: missing numeric total');
    }
    const result = res.data as NativeCalculateResult;
    // An unresolvable member comes back as member_card: { error }; collapse it to no coverage so
    // downstream readers (summary, selection panel, step gating) only ever see a full block or null.
    if (result.member_card?.error) result.member_card = null;
    return result;
}
